import { Icons } from '@/components/icons';
import TicketCuticle from '@/components/manage/TicketCuticle';
import PriceDisplay from '@/components/PriceDisplay';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useBookingSearch } from '@/hooks/useBookingSearch';
import { TransText } from '@/i18n/trans/text';
import TicketTripSummaries from '@/routes/account/tickets/TicketTripSummaries';
import TravelPassSummary from '@/routes/account/travelPasses/TravelPassSummary';
import { useSelector } from '@/store/utils';
import type { BookingSearchDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { format } from '@/utils/dateTime';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface BookingItemProps {
  booking: BookingSearchDTO;
  isTicket?: boolean;
  isActive: boolean;
}

const BookingItem: FC<BookingItemProps> = ({ booking, isTicket, isActive }) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const profile = useSelector(profileDataSelector);

  const outboundTripsSummaries = (booking.tripSummaries ?? []).filter(
    (trip) => trip.direction === 'OUTBOUND'
  );
  const inboundTripsSummaries = (booking.tripSummaries ?? []).filter(
    (trip) => trip.direction === 'INBOUND'
  );

  const rowStartTime = isTicket
    ? booking.journeyOrigin?.time
    : booking.travelPassDetails?.validFrom;
  const rowEndTime = isTicket
    ? booking.journeyDestination?.time
    : booking.travelPassDetails?.validUntil;
  const startTime = rowStartTime && format(rowStartTime, 'EEE, dd MMM yyyy');
  const endTime = rowEndTime && format(rowEndTime, 'EEE, dd MMM yyyy');
  const dateRange =
    startTime && endTime && startTime !== endTime
      ? `${startTime} - ${endTime}`
      : (startTime ?? endTime);
  const isRefunded = Boolean(
    booking.status &&
      ['REFUNDED', 'RELEASED', 'CANCELLED'].includes(booking.status)
  );

  const onBookingSearch = useBookingSearch();

  const renderHeaderTag = () => {
    if (!isRefunded && isActive) {
      return null;
    }

    const Icon = isRefunded ? Icons.ticketReturn : Icons.history;

    return (
      <div className="absolute -bottom-[11px] right-0.5 flex gap-1 rounded-full border border-neutral bg-white px-2 py-1 laptop:right-1.5">
        <Icon className="h-3 w-3 text-neutral" />
        <Typography variant="label" className="text-neutral">
          <TransText i18nKey={isRefunded ? 'refunded' : 'pastBooking'} />
        </Typography>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'relative flex justify-between rounded-t-lg border-b border-primary bg-primary-pale p-3 laptop:p-4',
          { 'border-neutral-light': isRefunded }
        )}
      >
        <div className="flex flex-col gap-0.5 laptop:flex-row laptop:items-center laptop:gap-2">
          <Typography
            variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
            className="text-dark"
          >
            <>
              <TransText i18nKey="booking" /> {booking.code}
            </>
          </Typography>
          {dateRange && (
            <Typography
              variant={isLaptopOrBigger ? 'body1' : 'body2'}
              className="text-neutral"
            >
              {dateRange}
            </Typography>
          )}
        </div>
        {!isRefunded && (
          <Typography
            variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
            className="text-primary"
          >
            <PriceDisplay amount={booking.totalPrice?.amount} />
          </Typography>
        )}
        {renderHeaderTag()}
      </div>
      <div
        className={cn(
          'flex flex-col gap-4 bg-white p-3 pb-6 text-dark laptop:flex-row laptop:p-4',
          { 'bg-primary-pale': isRefunded }
        )}
      >
        {isTicket ? (
          <TicketTripSummaries
            title="outboundTrips"
            tripSummaries={outboundTripsSummaries}
            isRefunded={isRefunded}
          />
        ) : (
          <TravelPassSummary
            travelPassSummary={booking.travelPassSummary}
            description={booking.travelPassDetails?.productDescription}
            passengerCount={booking.passengers?.length}
            isRefunded={isRefunded}
          />
        )}
        {Boolean(
          isTicket &&
            outboundTripsSummaries.length &&
            inboundTripsSummaries.length
        ) && (
          <>
            <Divider
              orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
              className="border-neutral-pale"
              grow={false}
            />
            <TicketTripSummaries
              title="returnTrips"
              tripSummaries={inboundTripsSummaries}
              isRefunded={isRefunded}
            />
          </>
        )}
      </div>
      <TicketCuticle isVertical={false} />
      <div
        className={cn(
          'flex flex-col gap-2 bg-white p-3 pt-6 laptop:grid laptop:grid-cols-3 laptop:p-4',
          { 'bg-primary-pale': isRefunded }
        )}
      >
        <Button
          data-testid="profile-booking-view-details"
          size="medium"
          variant={isRefunded ? 'tertiary' : 'secondary'}
          fullWidth
          className="laptop:col-start-3"
          onClick={() =>
            onBookingSearch({
              email: profile?.email,
              bookingNumber: booking.code,
            })
          }
        >
          <TransText i18nKey="viewDetails" />
          <Icons.arrowRight width={20} height={20} />
        </Button>
      </div>
    </div>
  );
};

export default BookingItem;
